import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT } from "const"
import { StateType, hasAccess } from "client/utils"
import {
    PRM_MARKOV_MODEL,
    StoreType,
    MarkovVariableFilters,

    LoadAllMarkovVariables,
    DeleteMarkovVariable,
    SortMarkovVariables,

    GetMarkovDatasetVariableExpressionVariableUsage,
    GetMarkovTransitionVariableUsage,
    GetMarkovStartvectorStateVariableUsage,
    GetMarkovRedistributionStateFromVariableUsage,
    GetMarkovRedistributionStateToVariableUsage,

    setMarkovVariableList,
    setMarkovVariableIds,
    setMarkovVariableDelete,
    setMarkovVariableFilters,
    setMarkovVariableReloadList,
    addModal,
} from "client/store"

import {
    Container,
    Loader,
    PageForm,
    FormGroup,
    Label,
    Button,
    Table,
    TableSearchWrapper,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
    SpinnerIcon,
} from "client/components"

import MarkovVariableSearch from "client/pages/search/markovVariableSearch"
import EditVariable from "client/pages/markov/variable/edit"
import Columns from "./columns"


const List = () => {
    const model = useSelector((state: StoreType) => state.markovModel.modelView)
    const stateList = useSelector((state: StoreType) => state.markovState.stateList)
    const datasets = useSelector((state: StoreType) => state.markovDataset.datasets)
    const datasetSelected = useSelector((state: StoreType) => state.markovDataset.datasetSelected)
    const startvectors = useSelector((state: StoreType) => state.markovStartvector.startvectors)
    const redistributions = useSelector((state: StoreType) => state.markovRedistribution.redistributions)

    const variableList = useSelector((state: StoreType) => state.markovVariable.variableList)
    const variableIds = useSelector((state: StoreType) => state.markovVariable.variableIds)
    const variableDelete = useSelector((state: StoreType) => state.markovVariable.variableDelete)
    const sortColumn = useSelector((state: StoreType) => state.markovVariable.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.markovVariable.sortOrder)
    const filters = useSelector((state: StoreType) => state.markovVariable.filters)
    const reloadList = useSelector((state: StoreType) => state.markovVariable.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [loadDeleteState, setLoadDeleteState] = useState<StateType>(IDLE)
    const [deleteState, setDeleteState] = useState<StateType>(IDLE)
    const [deleteWarning, setDeleteWarning] = useState<any>(null)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllMarkovVariables(model && model.id, datasetSelected && datasetSelected.id).then((res) => {
            dispatch(setMarkovVariableList(res.variableList, res.variableListByName))
            setLoadState(IDLE)
        })
    }, [model, datasetSelected, reloadList])

    useEffect(() => {
        const sortedIds = SortMarkovVariables(Object.values(variableList), sortColumn, sortOrder, filters)
        dispatch(setMarkovVariableIds(sortedIds))
    }, [variableList, sortColumn, sortOrder, filters])

    useEffect(() => {
        if (!variableDelete) return
        setLoadDeleteState(PENDING)
        Promise.all([
            GetMarkovDatasetVariableExpressionVariableUsage(variableDelete.id),
            GetMarkovTransitionVariableUsage(variableDelete.id),
            GetMarkovStartvectorStateVariableUsage(variableDelete.id),
            GetMarkovRedistributionStateFromVariableUsage(variableDelete.id),
            GetMarkovRedistributionStateToVariableUsage(variableDelete.id),
        ]).then(([
            datasetExpressions,
            transitions,
            startvectorStates,
            redistributionStatesFrom,
            redistributionStatesTo,
        ]) => {
            const variableUsageList: string[] = []

            // TODO: add to lang
            datasetExpressions.forEach((datasetExpression) => {
                const dataset = datasets.find((row) => row.id === datasetExpression.datasetId)
                const variable = variableList[datasetExpression.variableId]
                variableUsageList.push(`Dataset "${dataset && dataset.name}" variable "${variable && variable.name}"`)
            })
            transitions.forEach((transition) => {
                const stateFrom = stateList[transition.stateFrom]
                const stateTo = stateList[transition.stateTo]
                variableUsageList.push(`Transition state from "${stateFrom && stateFrom.name}" to "${stateTo && stateTo.name}"`)
            })
            startvectorStates.forEach((startvectorState) => {
                const startvector = startvectors.find((row) => row.id === startvectorState.startvectorId)
                const modelState = stateList[startvectorState.stateId]
                variableUsageList.push(`Startvector "${startvector && startvector.name}" state "${modelState && modelState.name}"`)
            })
            redistributionStatesFrom.forEach((redistributionState) => {
                const redistribution = redistributions.find((row) => row.id === redistributionState.redistributionId)
                const modelState = stateList[redistributionState.stateId]
                // eslint-disable-next-line max-len
                variableUsageList.push(`Redistribution "${redistribution && redistribution.name}" "From" state "${modelState && modelState.name}"`)
            })
            redistributionStatesTo.forEach((redistributionState) => {
                const redistribution = redistributions.find((row) => row.id === redistributionState.redistributionId)
                const modelState = stateList[redistributionState.stateId]
                // eslint-disable-next-line max-len
                variableUsageList.push(`Redistribution "${redistribution && redistribution.name}" "To" state "${modelState && modelState.name}"`)
            })

            if (variableUsageList.length) {
                const warningContent = (
                    <div>
                        <p>This variable is used in:</p>
                        {
                            variableUsageList.length && (
                                <ul>{ variableUsageList.map((row) => <li>{row}</li>) }</ul>
                            )
                        }
                        <p>It is recommended to fix those before attempting to delete variable.</p>
                    </div>
                )
                setDeleteWarning(warningContent)
            } else {
                setDeleteWarning(null)
            }

            setLoadDeleteState(IDLE)
        })
    }, [variableDelete])

    const onFilter = (newFilters: MarkovVariableFilters) => {
        dispatch(setMarkovVariableFilters(newFilters))
    }

    const onCancelDelete = () => {
        dispatch(setMarkovVariableDelete(null))
    }

    const onConfirmDelete = () => {
        setDeleteState(PENDING)
        DeleteMarkovVariable(variableDelete.id, model.id).then(() => {
            setDeleteState(IDLE)
            dispatch(setMarkovVariableDelete(null))

            dispatch(setMarkovVariableReloadList(false))
            setTimeout(() => {
                dispatch(setMarkovVariableReloadList(true))
            }, 0)
        })
    }

    const columns = Columns()
    const editModelAccess = hasAccess(PRM_MARKOV_MODEL, EDIT)

    return (
        <Container>
            {
                loadState === PENDING
                && <Loader />
            }
            <PageForm noPaddingTop noPaddingBottom>
                <FormGroup>
                    <Label>{lang.variables}</Label>
                </FormGroup>
            </PageForm>
            <TableSearchWrapper>
                <MarkovVariableSearch
                    id="variableList"
                    rightOffset={editModelAccess}
                    filters={filters}
                    onFilter={onFilter}
                />
                {
                    editModelAccess && (
                        <Button
                            variant="success"
                            onClick={() => dispatch(addModal({
                                key: "addVariable",
                                content: <EditVariable variableId={0} />,
                            }))}
                        >
                            {lang.add}
                        </Button>
                    )
                }
            </TableSearchWrapper>
            <Table
                columns={columns}
                dataList={variableList}
                dataIds={variableIds}
                rowSize="sm"
                showPagination
                scrollToTopOnPagination={false}
            />

            <ConfirmWrapper show={variableDelete !== null} onClose={onCancelDelete}>
                <Confirm align="start">
                    <ConfirmTitle>{variableDelete && variableDelete.name}</ConfirmTitle>
                    <ConfirmDescription>{lang.deleteVariableConfirm}</ConfirmDescription>

                    {
                        deleteWarning
                        && <ConfirmDescription>{deleteWarning}</ConfirmDescription>
                    }

                    <ConfirmBottom>
                        <Button
                            row
                            disabled={loadDeleteState === PENDING}
                            onClick={onCancelDelete}
                        >
                            {lang.cancel}
                        </Button>
                        <Button
                            row
                            disabled={loadDeleteState === PENDING || deleteWarning !== null}
                            variant="success"
                            onClick={onConfirmDelete}
                        >
                            {deleteState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </Container>
    )
}
export default List
